import {
	CdkDrag,
	CdkDragDrop,
	CdkDropList,
	moveItemInArray,
} from '@angular/cdk/drag-drop';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	input,
	OnDestroy,
	OnInit,
	QueryList,
	ViewChildren,
} from '@angular/core';
import {
	FormBuilder,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import {
	NgbActiveModal,
	NgbModalOptions,
	NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { PartialOptions } from 'overlayscrollbars';
import { AsyncSubject, merge, of, Subject } from 'rxjs';
import { filter, startWith, takeUntil } from 'rxjs/operators';
import { ConfirmPopupService } from 'src/lib/services/utility/confirm-popup/confirm-popup.service';
import { ModalService } from 'src/lib/services/utility/modal/modal.service';
import { FormControlWrapper } from 'src/lib/types/forms.def';
import { arrayUnion } from 'src/lib/utilities/array';
import {
	isEquivalentArray,
	isNullOrEmptyString,
	isString,
} from 'src/lib/utilities/compare';
import { generateGuid } from 'src/lib/utilities/random';
import { singleSearch } from 'src/lib/utilities/search';
import { firstBy } from 'thenby';
import { OverlayScrollbarsDragdropComponent } from '../../../../../thirdparty/overlay-scrollbars/overlay-scrollbars-dragdrop/overlay-scrollbars-dragdrop.component';
import { ModalMoverComponent } from '../../../../global/modal-mover/modal-mover.component';
import { PaginationTableColumnDirective } from '../../children/pagination-table-column.directive';
import {
	InternalPaginationTable,
	protectedPaginationTableConfigurationDefaultName,
} from '../../core/internal-pagination-table.interface';
import {
	columnStyleRuleClassSet,
	columnStyleRuleComparisonSet,
	warningColumnWidth,
} from '../../core/pagination-table-configuration.data';
import {
	PaginationTableSavedConfiguration,
	PaginationTableSavedResizedColumnWidthsConfiguration,
	PaginationTableSavedVisibleColumnConfiguration,
	PaginationTableStyleRule,
} from '../../types/ipagination-table-user-preferences';
import {
	PaginationTableConfigurationEdit,
	PaginationTableConfigurationEditModalComponent,
} from '../pagination-table-configuration-edit-modal/pagination-table-configuration-edit-modal.component';

interface SearchFormGroup {
	show: FormControl<boolean>;
	search: FormControl<string>;
}

@Component({
	selector: 'ae-pagination-table-configuration-modal',
	templateUrl: './pagination-table-configuration-modal.component.html',
	styleUrls: ['./pagination-table-configuration-modal.component.scss'],
	imports: [
		CdkDrag,
		CdkDropList,
		FormsModule,
		ModalMoverComponent,
		NgClass,
		NgSelectModule,
		NgTemplateOutlet,
		OverlayScrollbarsDragdropComponent,
		ReactiveFormsModule,
	],
})
export class PaginationTableConfigurationModalComponent<T>
	implements AfterViewInit, OnDestroy, OnInit
{
	private _unsubscribe$ = new AsyncSubject<null>();
	private _afterContentChecked$ = new Subject<null>();

	modal_ConfigurationModalRef: NgbModalRef;
	modal_SortedColumns: PaginationTableColumnDirective<T>[];
	modal_ExpandedStyleRules: Record<string, Record<string, boolean>> = {};

	modal_ColumnSelectorScrollOptions: PartialOptions = {
		overflow: {
			y: 'scroll',
			x: 'hidden',
		},
		scrollbars: {
			autoHide: 'never',
		},
	};

	private modal_originalTitleNodes: Element[];
	private modal_originalTitleHTML: Map<Element, string>;
	private permissionSearchHighlight = {
		start: '<span class="fw-bold bg-yellow">',
		end: '</span>',
	};
	public searchForm: FormGroup<SearchFormGroup>;

	columnStyleRuleComparisons = columnStyleRuleComparisonSet;
	columnStyleRuleClasses = columnStyleRuleClassSet;

	readonly pgTable = input.required<InternalPaginationTable<T>>();

	public newColumns: string[] = [];
	public undersizedColumns: string[] = [];

	@ViewChildren('columnSelectorElement')
	columnSelectorElements!: QueryList<ElementRef<HTMLDivElement>>;

	constructor(
		private activeModal: NgbActiveModal,
		private cdref: ChangeDetectorRef,
		private confirmPopupService: ConfirmPopupService,
		private modalService: ModalService,
		private fb: FormBuilder,
	) {}

	ngAfterViewInit(): void {
		// Use ViewChildren to find all title elements within column selector elements
		const titleElements: Element[] = [];
		this.columnSelectorElements.forEach((elementRef) => {
			const titleElement = elementRef.nativeElement.querySelector(
				'[id*="configcontrols.column.title"]',
			);
			if (titleElement) {
				titleElements.push(titleElement);
			}
		});

		// Convert to NodeListOf-like structure for compatibility
		this.modal_originalTitleNodes = titleElements;
		this.modal_originalTitleHTML = new Map();

		// Store original HTML structure
		this.modal_originalTitleNodes.forEach((node) => {
			this.modal_originalTitleHTML.set(node, node.innerHTML);
		});
	}

	ngOnInit(): void {
		const pgTable = this.pgTable();
		const snapshot = pgTable.getColumns(true);
		this.modal_initSearch();

		merge(
			pgTable.userPrefForm.controls.activeConfiguration.valueChanges,
			of(pgTable.userPreferences.activeConfiguration),
		)
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((activeConfig: PaginationTableSavedConfiguration) => {
				this.modal_SortedColumns = snapshot.slice().sort(
					firstBy<PaginationTableColumnDirective<T>>((c) => {
						const i = (activeConfig.columnOrder || []).indexOf(c.columnId());
						return i !== -1 ? i : Number.MAX_SAFE_INTEGER;
					}),
				);
			});

		pgTable.userPrefForm.valueChanges
			.pipe(
				filter(() => pgTable.userPrefFormIgnoreChangesCount === 0),
				takeUntil(this._unsubscribe$),
			)
			.subscribe(() => {
				pgTable.redraw$.next(null);
				this.cdref.detectChanges();
			});

		// Check for new columns
		const configsToCheck = [
			pgTable.userPreferences.activeConfiguration?.visibleColumns,
			...(pgTable.userPreferences.savedConfigurations?.map(
				(sc) => sc.visibleColumns,
			) ?? []),
			pgTable.userPreferences.notifiedColumns?.reduce((acc, v) => {
				acc[v] = true;
				return acc;
			}, {} as PaginationTableSavedVisibleColumnConfiguration),
		].filter((x) => x != null);

		if (configsToCheck.length > 0) {
			this.newColumns = snapshot
				.map((x) => x.columnId())
				.filter((x) => configsToCheck.every((c) => c[x] == null));
		}

		const newNotifyColumns = arrayUnion(
			pgTable.userPreferences.notifiedColumns ?? [],
			snapshot.map((x) => x.columnId()),
		);

		if (
			!isEquivalentArray(
				newNotifyColumns,
				pgTable.userPreferences.notifiedColumns,
			)
		) {
			pgTable.userPrefForm.controls.notifiedColumns.setValue(newNotifyColumns);
		}

		// Check for warning width columns
		pgTable.userPrefForm.controls.activeConfiguration.controls.resizedColumnWidths.valueChanges
			.pipe(
				startWith(
					pgTable.userPrefForm.controls.activeConfiguration.controls
						.resizedColumnWidths.value,
				),
				takeUntil(this._unsubscribe$),
			)
			.subscribe((widths) => {
				this.undersizedColumns = [];
				Object.entries(widths).forEach(([key, val]) => {
					if (val < warningColumnWidth) {
						this.undersizedColumns.push(key);
					}
				});
			});
	}

	ngOnDestroy(): void {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
		this._afterContentChecked$.complete();
	}

	public modal_isColumnChecked = (
		column: PaginationTableColumnDirective<T>,
	) => {
		return (
			this.pgTable().userPreferences.activeConfiguration.visibleColumns[
				column.columnId()
			] || column.locked()
		);
	};

	public modal_isColumnResized = (
		column: PaginationTableColumnDirective<T>,
	) => {
		return (
			this.pgTable().userPreferences.activeConfiguration.resizedColumnWidths[
				column.columnId()
			] != null
		);
	};

	/*
	 *
	 * Configuration Modal Functions
	 *
	 */
	public modal_ResetStyleRules = () => {
		this.pgTable().userPrefForm.controls.activeConfiguration.controls.styleRules.clear();
	};

	public modal_ResetColumnResizing = (
		column?: PaginationTableColumnDirective<T>,
	) => {
		let newValue: PaginationTableSavedResizedColumnWidthsConfiguration;

		if (column) {
			newValue =
				this.pgTable().userPrefForm.controls.activeConfiguration.controls
					.resizedColumnWidths.value;
			delete newValue[column.columnId()];
		} else {
			newValue = {};
		}

		this.pgTable().userPrefForm.controls.activeConfiguration.controls.resizedColumnWidths.setValue(
			newValue,
		);
	};

	public modal_ResetColumnOrder = () => {
		this.pgTable().userPrefForm.controls.activeConfiguration.controls.columnOrder.setValue(
			[],
		);
	};

	public modal_IsStyleRuleExpanded = (columnId: string): boolean => {
		if (
			!this.modal_ExpandedStyleRules[
				this.pgTable().userPreferences.activeConfiguration.configId
			]
		) {
			this.modal_ExpandedStyleRules[
				this.pgTable().userPreferences.activeConfiguration.configId
			] = {};
		}

		return Boolean(
			this.modal_ExpandedStyleRules[
				this.pgTable().userPreferences.activeConfiguration.configId
			][columnId],
		);
	};

	public modal_ToggleStyleRuleExpanded = (
		columnId: string,
		open?: boolean | null,
		element?: HTMLElement,
	): void => {
		if (
			!this.modal_ExpandedStyleRules[
				this.pgTable().userPreferences.activeConfiguration.configId
			]
		) {
			this.modal_ExpandedStyleRules[
				this.pgTable().userPreferences.activeConfiguration.configId
			] = {};
		}

		if (open == null) {
			open =
				!this.modal_ExpandedStyleRules[
					this.pgTable().userPreferences.activeConfiguration.configId
				][columnId];
		}

		this.modal_ExpandedStyleRules[
			this.pgTable().userPreferences.activeConfiguration.configId
		][columnId] = open;

		if (open) {
			setTimeout(() => {
				if (element != null) {
					// Overlayscrollbars doesn't have scroll anymore https://github.com/KingSora/OverlayScrollbars/issues/445
					element.scrollIntoView({
						behavior: 'smooth',
						block: 'nearest',
						inline: 'nearest',
					});
				}
			}, 100);
		}
	};

	public modal_ToggleAllStyleRuleExpanded = (): void => {
		const allColumns = this.pgTable().getColumns(true);
		const anyOpen =
			allColumns.find((x) => this.modal_IsStyleRuleExpanded(x.columnId())) !=
			null;
		allColumns.forEach((x) => {
			this.modal_ToggleStyleRuleExpanded(x.columnId(), !anyOpen);
		});
	};

	public modal_GetColumnStyleRules = (
		columnId: string,
	): FormGroup<FormControlWrapper<PaginationTableStyleRule>>[] => {
		return this.pgTable().userPrefForm.controls.activeConfiguration.controls.styleRules.controls.filter(
			(x) => x.controls.columnId.value === columnId,
		);
	};

	public modal_AddColumnStyleRule = (columnId: string) => {
		const group = this.pgTable().load_AddStyleRuleFormGroup();
		group.controls.columnId.setValue(columnId);
	};

	public modal_DeleteStyleRule = (
		target: FormGroup<FormControlWrapper<PaginationTableStyleRule>>,
	) => {
		const index =
			this.pgTable().userPrefForm.controls.activeConfiguration.controls.styleRules.controls.findIndex(
				(x) => x === target,
			);
		if (index > -1) {
			this.pgTable().userPrefForm.controls.activeConfiguration.controls.styleRules.removeAt(
				index,
			);
		}
	};

	public modal_TryLoadSavedConfiguration = (configId: string) => {
		const foundConfig =
			this.pgTable().userPrefForm.controls.savedConfigurations.value.find(
				(x) => x.configId === configId,
			);

		if (foundConfig) {
			this.pgTable().load_SavedConfiguration(foundConfig);
		}
	};

	private modal_setSearch = (search: string) => {
		// First, restore all original HTML structure
		this.modal_originalTitleNodes.forEach((element) => {
			const originalHTML = this.modal_originalTitleHTML.get(element);
			if (originalHTML) {
				// Restore the original HTML structure (including Angular templates)
				element.innerHTML = originalHTML;
			}
		});

		if (isNullOrEmptyString(search) || !isString(search)) {
			return;
		}

		let firstMatchElement: Element | null = null;

		// Find and update matching elements with new search/replace
		this.modal_originalTitleNodes.forEach((element) => {
			const currentHTML = element.innerHTML;

			const res = singleSearch({
				search: search,
				target: currentHTML,
				fuzzyThreshold: 0.7,
			});
			if (res.match) {
				const highlights = res.highlights(
					this.permissionSearchHighlight.start,
					this.permissionSearchHighlight.end,
				);

				if (!firstMatchElement) {
					firstMatchElement = element;
				}
				// Set highlighted HTML
				element.innerHTML = highlights;
			}
		});

		// Scroll to first matching element if found
		if (firstMatchElement) {
			setTimeout(() => {
				// Find parent element to scroll to (usually the row containing the column)
				const parentElement =
					firstMatchElement.closest('.mb-1') ||
					firstMatchElement.closest('.column-action-bar') ||
					firstMatchElement.parentElement;

				if (parentElement) {
					parentElement.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
						inline: 'nearest',
					});
				} else {
					// Fallback to original element if no suitable parent found
					firstMatchElement.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
						inline: 'nearest',
					});
				}
			}, 100);
		}
	};

	private modal_initSearch = () => {
		const form = this.fb.group<SearchFormGroup>({
			search: new FormControl(null),
			show: new FormControl(false),
		});

		form.controls.show.valueChanges
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((v) => {
				if (v !== true) {
					form.controls.search.setValue(null);
				}
			});

		form.controls.search.valueChanges
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((v) => {
				this.modal_setSearch(v);
			});

		this.searchForm = form;
	};

	public dismiss = () => {
		this.activeModal.dismiss();
	};

	public drop = (e: CdkDragDrop<PaginationTableColumnDirective<T>[]>) => {
		moveItemInArray(e.container.data, e.previousIndex, e.currentIndex);
		this.pgTable().userPrefForm.controls.activeConfiguration.controls.columnOrder.setValue(
			e.container.data.map((c) => c.columnId()),
		);
	};

	/*
	 *
	 * Saved Configuration Management
	 *
	 */
	public savedConfiguration_OpenEditModal = (createNew: boolean) => {
		let name = null;
		if (
			!createNew &&
			this.pgTable().userPrefForm.controls.activeConfiguration.controls.name
				.value !== protectedPaginationTableConfigurationDefaultName
		) {
			name =
				this.pgTable().userPrefForm.controls.activeConfiguration.controls.name
					.value;
		}

		const ngbModalOptions: NgbModalOptions = {
			backdrop: 'static',
		};

		const modal = this.modalService.open(
			PaginationTableConfigurationEditModalComponent,
			ngbModalOptions,
			{
				pgTable: this.pgTable() as any,
				name: name,
				create: createNew,
			},
		);

		modal.result
			.then((val: PaginationTableConfigurationEdit) => {
				if (val.create) {
					const savedConfig = this.savedConfiguration_Create(
						val.name,
						val.copy,
					);
					this.pgTable().load_SavedConfiguration(savedConfig.value);
				} else {
					this.savedConfiguration_Rename(val.name);
				}
			})
			.catch(() => {
				// Do Nothing
			});
	};

	public savedConfiguration_OpenDeleteModal = () => {
		this.confirmPopupService
			.confirm$({
				message: `You are sure you want to delete ${this.pgTable().userPrefForm.controls.activeConfiguration.controls.name.value}`,
			})
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((x) => {
				if (x) {
					this.savedConfiguration_Delete(
						this.pgTable().userPrefForm.controls.activeConfiguration.controls
							.configId.value,
					);
					if (
						this.pgTable().userPrefForm.controls.savedConfigurations.length > 0
					) {
						this.pgTable().load_SavedConfiguration(
							this.pgTable().userPrefForm.controls.savedConfigurations.at(0)
								.value,
						);
					} else {
						this.pgTable().load_SavedConfiguration(
							this.pgTable().formBuild_SavedConfiguration_Group()
								.value as PaginationTableSavedConfiguration,
						);
					}
				}
			});
	};

	public savedConfiguration_Rename = (name: string) => {
		const activeConfig =
			this.pgTable().userPrefForm.controls.activeConfiguration;
		if (
			activeConfig.controls.name.value ===
			protectedPaginationTableConfigurationDefaultName
		) {
			this.savedConfiguration_Create(name, true);
		}

		activeConfig.controls.name.setValue(name);
	};

	public savedConfiguration_Create = (name: string, copyActive: boolean) => {
		this.pgTable().userPrefFormIgnoreChangesCount++;

		const saveControl = this.pgTable().formBuild_SavedConfiguration_Control();
		if (copyActive) {
			saveControl.patchValue(
				JSON.parse(
					JSON.stringify(
						this.pgTable().userPrefForm.controls.activeConfiguration.value,
					),
				),
			);
		}

		const saveVal = saveControl.value;
		saveVal.name = name;
		saveVal.configId = generateGuid();
		saveControl.setValue(saveVal);

		this.pgTable().userPrefForm.controls.savedConfigurations.push(saveControl);
		this.pgTable().userPrefFormIgnoreChangesCount--;

		return saveControl;
	};

	public savedConfiguration_Delete = (configId: string) => {
		const index =
			this.pgTable().userPrefForm.controls.savedConfigurations.controls.findIndex(
				(x) => x.value.configId === configId,
			);

		if (index > -1) {
			this.pgTable().userPrefForm.controls.savedConfigurations.removeAt(index);
		}
	};
}
